/**
 * Protected Route Component
 * Wrapper component that protects routes requiring authentication
 * Also initializes WebSocket connection for authenticated users
 */

import { useState, useEffect, useRef } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router";
import { useAtom } from "jotai";
import { useAuth, useLogout, isAuthenticatedAtom, currentUserAtom } from "@/hooks/use-auth";
import { useAccountFlags } from "@/hooks/use-account-flags";
import { useBusinessUnit } from "@/hooks/use-business-units";
import { useAuthSync } from "@/hooks/use-auth-sync";
import { usePermissionsFetcher } from "@/hooks/use-permissions-query";
import { CloudOff, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { WebSocketProvider } from "@/lib/websocket-provider";
import { authService } from "@/services";
import { clearQueryCache } from "@/lib/query-client";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const CURRENT_USER_KEY = "currentUser";

export default function ProtectedRoute() {
  const { isAuthenticated } = useAuth();
  const [, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
  const [, setCurrentUser] = useAtom(currentUserAtom);
  const logout = useLogout();
  const [searchParams] = useSearchParams();
  const ssoToken = searchParams.get("sso_token");
  const [ssoState, setSsoState] = useState<"idle" | "loading" | "error">(
    ssoToken ? "loading" : "idle",
  );
  const attemptedSsoTokenRef = useRef<string | null>(null);

  /**
   * The brand list, gated on BELOW so nothing authenticated renders until it has arrived.
   *
   * Subscribed HERE rather than in the sidebar so the request starts with the shell, and
   * `useBusinessUnit` (not `useBusinessUnits`) because the singular one also RESOLVES the
   * stored selection to the account's default and writes it back.
   */
  const { isLoading: brandsLoading, isError: brandsError } = useBusinessUnit();

  // Fetch account flags globally as the first call after login
  useAccountFlags();
  useAuthSync();
  // Fetch RBAC permissions once authenticated. Lives here (not in layouts)
  // so it runs before RouteGuard's perms-loading gate.
  usePermissionsFetcher();

  useEffect(() => {
    if (!ssoToken) {
      attemptedSsoTokenRef.current = null;
      setSsoState("idle");
      return;
    }

    if (attemptedSsoTokenRef.current === ssoToken) return;

    attemptedSsoTokenRef.current = ssoToken;
    setSsoState("loading");

    logout
      .mutateAsync()
      .then(() => authService.ssoLogin(ssoToken))
      .then((data) => {
        clearQueryCache();
        localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(data.user));
        setIsAuthenticated(true);
        setCurrentUser(data.user);
        window.history.replaceState({}, "", window.location.pathname);
        setSsoState("idle");
      })
      .catch(() => {
        setSsoState("error");
      });
  }, [ssoToken, setCurrentUser, setIsAuthenticated]);

  if (ssoState === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <img src="/assets/brand/kim-ai-logo.svg" alt="Kim AI" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground">Kim AI</h1>
          <p className="text-muted-foreground mt-2 mb-8">Signing you in…</p>
          <Spinner className="size-8 mx-auto" />
        </div>
      </div>
    );
  }

  if (ssoState === "error") {
    return <Navigate to="/login?error=sso_failed" replace />;
  }

  if (isAuthenticated === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  /**
   * NOTHING AUTHENTICATED RENDERS UNTIL THE BRAND IS KNOWN.
   *
   * Every scoped view is scoped to exactly one brand, and a WRITE that names no brand is
   * stored with `bu_id` NULL rather than under the account's default — a row no brand-scoped
   * read can reach afterwards. Previously the shell painted while the list was still in
   * flight, so a connect clicked in that window saved exactly that (account 843: a Shopify
   * store filed under no brand, every Shopify tool call answering "provider not connected").
   *
   * Narrowing that window is not the same as closing it. Holding the whole authenticated
   * tree until the list resolves means there is no interactive moment in which a brand is
   * unknown, so `bu_id` is always present by construction rather than by each caller
   * remembering to wait. It costs one request on first paint; after that localStorage
   * answers and `staleTime` keeps it cached.
   */
  if (brandsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  /**
   * A FAILED fetch fails closed; an EMPTY one does not.
   *
   * The two are different answers. An error means the brand is unknown, which is the state
   * that produced the unreachable row, so rendering anyway would reopen the hole. An empty
   * list is a settled fact — this account has no brand — and some genuinely do not (the
   * `system` account, older internal ones). Blocking those would lock their users out of
   * everything under this route, including /super-admin. A write from such an account has
   * no brand to carry in any case, so an absent `bu_id` there is the honest answer rather
   * than a race — which is the distinction this gate exists to preserve.
   *
   * Reload rather than a silent retry: the query has already exhausted its own retries by
   * the time this renders.
   */
  if (brandsError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-4">
        <div role="alert" className="flex w-full max-w-sm flex-col items-center text-center">
          <span className="mb-5 flex size-12 items-center justify-center rounded-full bg-red-50">
            <CloudOff className="size-5 text-red-500" />
          </span>
          <h1 className="text-[16px] font-semibold leading-6 text-text-body">We couldn’t load your workspace</h1>
          <p className="mt-1.5 text-[13px] leading-[18px] text-gray-800">
            Something went wrong while loading your brands. This is usually temporary, so try again in a moment.
          </p>
          <Button className="mt-6" onClick={() => window.location.reload()}>
            <RotateCw />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  // Render the protected route with WebSocket connection
  return (
    <WebSocketProvider>
      <Outlet />
    </WebSocketProvider>
  );
}
